import { useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import coralService from '../../../services/coral/coral-service';
import coralApi from '../../../api/coral-api';
import { transformMessageFromApi, sortGroupsByLastMessageTime } from '../../../services/coral/coral-utils';

export const useGroups = (filter = "all") => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState(null);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await coralService.getGroups(organizationId, filter);
      setGroups(sortGroupsByLastMessageTime(data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, filter]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Listen to org-wide websocket messages to update group list previews
  useEffect(() => {
    if (!organizationId) return;

    const socket = io(coralApi.getWsBaseUrl(), {
      path: '/ws',
      transports: ['websocket'],
      auth: {
        'x-api-key': coralApi.getApiKey(),
        'x-org-id': organizationId,
      },
    });

    const onMessage = (evt) => {
      // Expect evt.group_id to identify target group
      if (!evt?.group_id) return;

      const apiLike = {
        id: evt.wa_wamid,
        _id: evt.wa_wamid,
        wa_wamid: evt.wa_wamid,
        author: evt.author,
        author_number: evt.authorNumber,
        content: evt.content,
        sent_at: evt.sentAt,
        is_alert: Boolean(evt.alertId),
        is_template: Boolean(evt.is_template),
        parameters: evt.parameters || [],
        alert_id: evt.alertId || null,
        csw_open: evt.csw_open || false,
      };

      const uiMessage = transformMessageFromApi(apiLike);

      setGroups((prev) => {
        let found = false;
        const updated = prev.map((g) => {
          if (g.id === evt.group_id) {
            found = true;
            return {
              ...g,
              lastMessage: uiMessage.content,
              lastMessageTime: uiMessage.timestamp,
              lastMessageRawTime: uiMessage.rawTimestamp,
              // Use server-provided unread count when available
              unreadCount: typeof evt.unread_count === 'number' ? evt.unread_count : g.unreadCount,
              cswOpen: evt.csw_open || false,
            };
          }
          return g;
        });

        if (!found) return prev;

        // Sort by most recent raw timestamp desc (fallback to lastMessageTime)
        return sortGroupsByLastMessageTime(updated);
      });
    };

    socket.on('message', onMessage);

    return () => {
      try { socket.off('message', onMessage); } catch (_) {}
      try { socket.disconnect(); } catch (_) {}
    };
  }, [organizationId]);

  const createGroup = async (groupData) => {
    setError(null);
    setCreateLoading(true);
    try {
      const newGroup = await coralService.createGroup(organizationId, groupData);
      await fetchGroups();
      return newGroup;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setCreateLoading(false);
    }
  };

  const editGroup = async (groupId, groupData) => {
    setEditLoading(true);
    setError(null);
    try {
      const editedGroup = await coralService.editGroup(organizationId, groupId, groupData);
      await fetchGroups();
      return editedGroup;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setEditLoading(false);
    }
  };

  return { groups, loading, editLoading, createLoading, error, refetch: fetchGroups, createGroup, editGroup };
};

export const useGroupMessages = (groupId, onNewMessages) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [lastFetchedMessageIds, setLastFetchedMessageIds] = useState(new Set());
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRealtime, setIsRealtime] = useState(false);
  const [socketRef, setSocketRef] = useState(null);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  // Sort messages by timestamp (oldest first, newest at bottom)
  const sortMessagesByTime = (messages) => {
    return [...messages].sort((a, b) => {
      const dateA = new Date(a.rawTimestamp || a.timestamp);
      const dateB = new Date(b.rawTimestamp || b.timestamp);
      return dateA - dateB;
    });
  };

  const fetchMessages = useCallback(async (pageNum = 1, isLoadMore = false) => {
    if (!groupId) return;

    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await coralService.getGroupMessages(organizationId, groupId, pageNum);

      if (pageNum === 1 && !isLoadMore) {
        // Initial load or refresh
        const sortedMessages = sortMessagesByTime(response.messages);
        setMessages(sortedMessages);
        const messageIds = new Set(sortedMessages.map(m => m.id));
        setLastFetchedMessageIds(messageIds);
      } else {
        // Loading more (older messages)
        setMessages(prev => {
          const combined = [...response.messages, ...prev];
          return sortMessagesByTime(combined);
        });
      }

      setPage(pageNum);
      setHasMore(response.hasMore);
      setError(null);
    } catch (error) {
      setError(error.message);
      if (pageNum === 1 && !isLoadMore) {
        setMessages([]);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [organizationId, groupId]);

  // Silent refresh to get new messages
  const silentRefresh = useCallback(async () => {
    if (!groupId) return;

    try {
      const response = await coralService.getGroupMessages(organizationId, groupId, 1);

      let hasNewMessages = false;

      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        // Filter new messages that aren't from the platform and aren't already in the list
        const newMessages = response.messages.filter(m =>
          !existingIds.has(m.id) && !m.isPlatform
        );

        if (newMessages.length > 0) {
          hasNewMessages = true;
          const combined = [...prev, ...newMessages];
          return sortMessagesByTime(combined);
        }
        return prev;
      });

      // Return whether new messages were added
      return hasNewMessages;
    } catch (error) {
      console.error('Silent refresh error:', error);
      return false;
    }
  }, [organizationId, groupId]);

  // Set up and clean up refresh interval
  useEffect(() => {
    if (groupId && !isRealtime) {
      // Clear any existing interval
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }

      // Set up new interval for silent refresh every 3 seconds
      const interval = setInterval(async () => {
        const hasNewMessages = await silentRefresh();
        if (hasNewMessages && onNewMessages) {
          onNewMessages();
        }
      }, 3000);

      setRefreshInterval(interval);

      // Cleanup on unmount or group change
      return () => {
        clearInterval(interval);
      };
    } else {
      // Clear interval if no groupId
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  }, [groupId, onNewMessages, isRealtime]); // Include onNewMessages and isRealtime in deps

  // Real-time Socket.IO connection for new messages
  useEffect(() => {
    // Clean up any existing socket when group changes or unmounts
    return () => {
      if (socketRef) {
        try { socketRef.off('message'); } catch (_) {}
        try { socketRef.disconnect(); } catch (_) {}
      }
      setSocketRef(null);
      setIsRealtime(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    try {
      const socket = io(coralApi.getWsBaseUrl(), {
        path: '/ws',
        transports: ['websocket'],
        auth: {
          'x-api-key': coralApi.getApiKey(),
          'x-org-id': organizationId,
        },
        query: {
          group_id: groupId,
        },
      });

      // On connect, mark realtime active (disables polling)
      socket.on('connect', () => {
        setIsRealtime(true);
      });

      // Connection errors (e.g., auth failure, CORS, bad path)
      socket.on('connect_error', (err) => {
        console.error('Socket connect_error:', err);
        setError(err?.message || 'Realtime connection failed');
        setIsRealtime(false);
      });

      socket.on('error', (err) => {
        console.error('Socket error:', err);
      });

      // Handle incoming message events
      socket.on('message', (evt) => {
        console.log("evt", evt)
        // Server emits all messages for the organization (room: org:<org_id>)
        // Only process messages for the currently viewed group
        if (evt?.group_id && evt.group_id !== groupId) {
          return;
        }
        // evt shape:
        // { organizationId, group_id, wa_wamid, author, authorNumber, content, sentAt, origin, alertId? }
        const apiLike = {
          id: evt.wa_wamid, // if provided
          _id: evt.wa_wamid, // for safety with transformer
          wa_wamid: evt.wa_wamid,
          author: evt.author,
          author_number: evt.authorNumber,
          content: evt.content,
          displayed_content: evt.displayed_content ?? evt.displayedContent,
          sent_at: evt.sentAt,
          origin: evt.origin,
          is_alert: Boolean(evt.alertId),
          is_template: Boolean(evt.is_template),
          parameters: evt.parameters || [],
          alert_id: evt.alertId || null,
          csw_open: evt.csw_open || false,
        };

        if (evt.origin !== "platform" || apiLike.is_template) {
          const uiMessage = transformMessageFromApi(apiLike);

          setMessages((prev) => {
            const exists = prev.some(m => m.id === uiMessage.id);
            if (exists) return prev;
            const combined = [...prev, uiMessage];
            return combined.sort((a, b) => {
              const dateA = new Date(a.rawTimestamp || a.timestamp);
              const dateB = new Date(b.rawTimestamp || b.timestamp);
              return dateA - dateB;
            });
          });

          if (onNewMessages) onNewMessages();
        }
      });

      socket.on('disconnect', () => {
        setIsRealtime(false);
      });

      setSocketRef(socket);

      return () => {
        try { socket.off('message'); } catch (_) {}
        try { socket.disconnect(); } catch (_) {}
      };
    } catch (e) {
      // If URL parsing fails, fall back to polling
      console.error('Realtime connection setup failed:', e);
      setIsRealtime(false);
    }
  }, [groupId, organizationId, onNewMessages]);

  useEffect(() => {
    if (groupId) {
      setMessages([]);
      setLoading(true);
      setError(null);
      setPage(1);
      setHasMore(true);
      fetchMessages(1);
    }
  }, [groupId, fetchMessages]);

  const loadMore = () => {
    if (!loading && !isLoadingMore && hasMore) {
      fetchMessages(page + 1, true);
    }
  };

  const sendMessage = async (message) => {
    setError(null);

    // Create an optimistic message that will show immediately
    const optimisticMessage = {
      id: `temp-${Date.now()}-${Math.random()}`,
      sender: 'You',
      senderNumber: '',
      content: message,
      timestamp: 'Just now',
      rawTimestamp: new Date().toISOString(),
      isOwn: true,
      isPlatform: true,
      isAlert: false,
      alertId: null,
      wamid: null,
    };

    // Add the message immediately to the UI
    setMessages(prev => sortMessagesByTime([...prev, optimisticMessage]));

    try {
      if (socketRef && socketRef.connected) {
        socketRef.emit('sendMessage', {
          group_id: groupId,
          message,
        });
      } else {
        // Just send the message to the API, don't add the response to messages
        // The message will stay as the optimistic one we already added
        await coralService.sendMessage(organizationId, groupId, message);
      }
      // Keep the optimistic message as is - don't replace it
      return optimisticMessage;
    } catch (err) {
      // Remove the optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
      setError(err.message);
      throw err;
    }
  };

  const sendTemplate = async (templateName, parameters) => {
    setError(null);

    // Create an optimistic message that will show immediately
    // const optimisticMessage = {
    //   id: `temp-${Date.now()}-${Math.random()}`,
    //   sender: 'You',
    //   senderNumber: '',
    //   content: "Some optimistic content",
    //   timestamp: 'Just now',
    //   rawTimestamp: new Date().toISOString(),
    //   isOwn: true,
    //   isPlatform: true,
    //   isAlert: false,
    //   alertId: null,
    //   wamid: null,
    //   isTemplate: true,
    // };

    // Add the message immediately to the UI
    // setMessages(prev => sortMessagesByTime([...prev, optimisticMessage]));
    try {
      if (socketRef && socketRef.connected) {
        socketRef.emit('sendTemplateMessage', {
          group_id: groupId,
          templateName,
          parameters,
        });
      } else {
        await coralService.sendTemplate(organizationId, groupId, templateName, parameters);
      }
      // return optimisticMessage;
    } catch (err) {
      // Remove the optimistic message on error
      // setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
      setError(err.message);
      throw err;
    }
  }

  return {
    messages,
    loading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    sendMessage,
    sendTemplate,
    refetch: () => fetchMessages(1),
  };
};

export const useAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await coralService.getAllAlerts(organizationId);
      setAlerts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const sendAlertEmail = async (alertId, email, subject, body) => {
    setError(null);
    try {
      const response = await coralService.sendAlertEmail(organizationId, alertId, email, subject, body);
      await fetchAlerts();
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return { alerts, loading, error, refetch: fetchAlerts, sendAlertEmail };
};

export const useGroupParticipants = (groupId) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const fetchParticipants = useCallback(async () => {
    if (!groupId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await coralService.getGroupParticipants(organizationId, groupId);
      setParticipants(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, groupId]);

  useEffect(() => {
    // Clear participants immediately when groupId changes
    setParticipants([]);
    setError(null);
    
    if (organizationId && groupId) {
      fetchParticipants();
    }
  }, [organizationId, groupId, fetchParticipants]);

  // Listen for websocket events indicating a user joined or left this group,
  // and refresh the participants list when that happens.
  useEffect(() => {
    if (!organizationId || !groupId) return;

    const socket = io(coralApi.getWsBaseUrl(), {
      path: '/ws',
      transports: ['websocket'],
      auth: {
        'x-api-key': coralApi.getApiKey(),
        'x-org-id': organizationId,
      },
    });

    const handleUserJoinedGroup = (evt) => {
      // evt shape from backend: { organizationId, group_id, member? }
      if (!evt?.group_id || evt.group_id !== groupId) return;
      fetchParticipants();
    };

    const handleUserLeftGroup = (evt) => {
      // evt shape from backend: { organizationId, group_id, member? }
      if (!evt?.group_id || evt.group_id !== groupId) return;
      fetchParticipants();
    };

    socket.on('userJoinedGroup', handleUserJoinedGroup);
    socket.on('userLeftGroup', handleUserLeftGroup);

    return () => {
      try { socket.off('userJoinedGroup', handleUserJoinedGroup); } catch (_) {}
      try { socket.off('userLeftGroup', handleUserLeftGroup); } catch (_) {}
      try { socket.disconnect(); } catch (_) {}
    };
  }, [organizationId, groupId, fetchParticipants]);

  const addParticipant = async (participantData) => {
    setError(null);
    setLoading(true);
    try {
      await coralService.addGroupParticipants(organizationId, groupId, [participantData]);
      await fetchParticipants();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeParticipant = async (phoneNumber) => {
    setError(null);
    setLoading(true);
    try {
      await coralService.removeGroupParticipant(organizationId, groupId, phoneNumber);
      await fetchParticipants();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    participants,
    loading,
    error,
    addParticipant,
    removeParticipant,
    refetch: fetchParticipants,
  };
};

export const useGroupDetails = (groupId) => {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const fetchGroupDetails = useCallback(async () => {
    if (!groupId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await coralService.getGroupWithDetails(organizationId, groupId);
      setGroup(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, groupId]);

  useEffect(() => {
    if (organizationId && groupId) {
      fetchGroupDetails();
    }
  }, [organizationId, groupId, fetchGroupDetails]);

  return { group, loading, error, refetch: fetchGroupDetails };
};

export const useOrganizationSettings = () => {
  const [organizationSettings, setOrganizationSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const fetchOrganizationSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await coralService.getOrganizationSettings(organizationId);
      setOrganizationSettings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const updateOrganizationSettings = async (organizationSettings) => {
    setLoading(true);
    setError(null);
    try {
      const data = await coralService.updateOrganizationSettings(organizationId, organizationSettings);
      setOrganizationSettings(data);
      await fetchOrganizationSettings();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchOrganizationSettings();
    }
  }, [organizationId, fetchOrganizationSettings]);

  return { organizationSettings, loading, error, refetch: fetchOrganizationSettings, updateOrganizationSettings };
};